import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import { storiesAPI } from '../utils/api';
import { toast } from 'sonner';

const REACTIONS = ['❤️', '🔥', '😂', '😮', '😢', '👏'];

const StoriesViewer = ({ stories, initialUserIndex, onClose, onStoryDeleted }) => {
  const [currentUserIndex, setCurrentUserIndex] = useState(initialUserIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [showReactionFeedback, setShowReactionFeedback] = useState(null);
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  const currentUserStories = stories[currentUserIndex];
  const currentStory = currentUserStories?.stories?.[currentStoryIndex];
  const isOwnStory = currentStory?.user_id === currentUser?.id;

  const storyIndexRef = useRef(currentStoryIndex);
  const userIndexRef = useRef(currentUserIndex);
  const pausedRef = useRef(paused);

  useEffect(() => { storyIndexRef.current = currentStoryIndex; }, [currentStoryIndex]);
  useEffect(() => { userIndexRef.current = currentUserIndex; }, [currentUserIndex]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const nextStory = useCallback(() => {
    const si = storyIndexRef.current;
    const ui = userIndexRef.current;
    const userStories = stories[ui];
    if (!userStories) { onClose(); return; }

    if (si < userStories.stories.length - 1) {
      setCurrentStoryIndex(si + 1);
      setProgress(0);
    } else if (ui < stories.length - 1) {
      setCurrentUserIndex(ui + 1);
      setCurrentStoryIndex(0);
      setProgress(0);
    } else {
      onClose();
    }
  }, [stories, onClose]);

  const prevStory = useCallback(() => {
    const si = storyIndexRef.current;
    const ui = userIndexRef.current;

    if (si > 0) {
      setCurrentStoryIndex(si - 1);
      setProgress(0);
    } else if (ui > 0) {
      const prevUserStories = stories[ui - 1];
      setCurrentUserIndex(ui - 1);
      setCurrentStoryIndex(prevUserStories.stories.length - 1);
      setProgress(0);
    }
  }, [stories]);

  useEffect(() => {
    if (!currentStory) return;
    storiesAPI.view(currentStory.id).catch(() => {});
  }, [currentStory]);

  useEffect(() => {
    if (!currentStory) return;
    const duration = 5000;
    const interval = 50;
    const increment = (interval / duration) * 100;

    const timer = setInterval(() => {
      if (pausedRef.current) return;
      setProgress((prev) => {
        if (prev >= 100) {
          nextStory();
          return 0;
        }
        return prev + increment;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [currentUserIndex, currentStoryIndex, nextStory, currentStory]);

  const handleDelete = async () => {
    if (!currentStory) return;
    try {
      await storiesAPI.delete(currentStory.id);
      toast.success('Story deleted');
      if (onStoryDeleted) onStoryDeleted();
      onClose();
    } catch {
      toast.error('Failed to delete story');
    }
  };

  const handleReaction = async (emoji) => {
    if (!currentStory) return;
    setPaused(true);
    setShowReactionFeedback(emoji);
    try {
      await storiesAPI.react(currentStory.id, emoji);
    } catch {
      // silent fail
    }
    setTimeout(() => {
      setShowReactionFeedback(null);
      setPaused(false);
    }, 1200);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextStory();
      else if (e.key === 'ArrowLeft') prevStory();
      else if (e.key === 'Escape') onClose();
      else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextStory, prevStory, onClose]);

  if (!currentStory) return null;

  const isTextStory = currentStory.media_type === 'text';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black z-50 flex items-center justify-center"
        data-testid="stories-viewer"
      >
        {/* Progress bars */}
        <div className="absolute top-4 left-4 right-4 flex space-x-1 z-10" data-testid="story-progress-bars">
          {currentUserStories.stories.map((_, index) => (
            <div key={`progress-${index}`} className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full"
                style={{
                  width: index < currentStoryIndex ? '100%' : index === currentStoryIndex ? `${progress}%` : '0%',
                  transition: index === currentStoryIndex ? 'width 50ms linear' : 'none'
                }}
              />
            </div>
          ))}
        </div>

        {/* User info */}
        <div className="absolute top-8 left-4 flex items-center space-x-3 z-10">
          <img
            src={currentUserStories.user?.avatar_url || 'https://via.placeholder.com/40'}
            alt={currentUserStories.user?.full_name || 'User'}
            className="w-10 h-10 rounded-full border-2 border-white object-cover"
            data-testid="story-user-avatar"
          />
          <div>
            <p className="text-white font-bold" data-testid="story-user-name">
              {currentUserStories.user?.full_name || 'Unknown User'}
            </p>
            <p className="text-white/70 text-sm">
              {currentStory.created_at ? new Date(currentStory.created_at).toLocaleTimeString() : ''}
            </p>
          </div>
        </div>

        {/* Close & Delete buttons */}
        <div className="absolute top-8 right-4 flex items-center space-x-3 z-10">
          {isOwnStory && (
            <button onClick={handleDelete} className="text-white/80 hover:text-red-400 transition-colors" data-testid="delete-story-btn" title="Delete story">
              <FaTrash className="text-lg" />
            </button>
          )}
          <button onClick={onClose} className="text-white hover:text-gray-300" data-testid="close-stories-btn">
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Pause indicator */}
        {paused && !showReactionFeedback && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 text-white/50 text-sm">Paused</div>
        )}

        {/* Reaction feedback animation */}
        {showReactionFeedback && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1.5, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 text-6xl"
            data-testid="reaction-feedback"
          >
            {showReactionFeedback}
          </motion.div>
        )}

        {/* Story content */}
        <div className="relative w-full max-w-lg h-full flex items-center justify-center" data-testid="story-content"
          onMouseDown={() => setPaused(true)}
          onMouseUp={() => setPaused(false)}
          onMouseLeave={() => setPaused(false)}
        >
          {isTextStory ? (
            <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${currentStory.background || 'from-blue-500 to-purple-600'}`}>
              <p className="text-white text-2xl font-bold text-center px-8 max-w-md">
                {currentStory.content}
              </p>
            </div>
          ) : currentStory.media_type?.startsWith('image') ? (
            <img src={currentStory.media_url} alt="Story" className="max-w-full max-h-full object-contain" data-testid="story-image" />
          ) : currentStory.media_type?.startsWith('video') ? (
            <video src={currentStory.media_url} className="max-w-full max-h-full object-contain" autoPlay muted data-testid="story-video" />
          ) : null}

          {!isTextStory && currentStory.content && (
            <div className="absolute bottom-24 left-4 right-4 p-4 bg-black/50 rounded-lg">
              <p className="text-white" data-testid="story-caption">{currentStory.content}</p>
            </div>
          )}

          {/* Click navigation areas */}
          <button onClick={prevStory} className="absolute left-0 top-0 bottom-16 w-1/3" aria-label="Previous story" data-testid="story-nav-prev" />
          <button onClick={nextStory} className="absolute right-0 top-0 bottom-16 w-1/3" aria-label="Next story" data-testid="story-nav-next" />
        </div>

        {/* Reaction bar at bottom */}
        {!isOwnStory && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 z-10" data-testid="story-reactions-bar">
            {REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleReaction(emoji)}
                className="text-2xl hover:scale-125 transition-transform bg-white/10 rounded-full w-11 h-11 flex items-center justify-center backdrop-blur-sm"
                data-testid={`story-reaction-${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Navigation arrows */}
        {currentUserIndex > 0 && (
          <button
            onClick={() => { setCurrentUserIndex(currentUserIndex - 1); setCurrentStoryIndex(0); setProgress(0); }}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
            data-testid="story-prev-user-btn"
          >
            <FaChevronLeft className="text-3xl" />
          </button>
        )}
        {currentUserIndex < stories.length - 1 && (
          <button
            onClick={() => { setCurrentUserIndex(currentUserIndex + 1); setCurrentStoryIndex(0); setProgress(0); }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
            data-testid="story-next-user-btn"
          >
            <FaChevronRight className="text-3xl" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default StoriesViewer;
